import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ElementType, useEffect, useState } from 'react';
import { CheckCircle, Clock, Plus, XCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getUtilityTemplates } from '../../../hooks/facebook_utility_messages';
import { FacebookUtilityMessage } from '../../../utils/types';

export default function FacebookUtilityMessagesListPage() {
  const { orgId, profileId } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<FacebookUtilityMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await getUtilityTemplates(orgId || '', profileId || '');
      if (res.success) setTemplates(Array.isArray(res.data) ? res.data : []);
      setLoading(false);
    })();
  }, [orgId, profileId]);

  const statusMeta = (status: string) => {
    switch (status) {
      case 'approved':
        return { color: 'green', icon: CheckCircle, label: 'Approved' };
      case 'rejected':
        return { color: 'red', icon: XCircle, label: 'Rejected' };
      default:
        return { color: 'orange', icon: Clock, label: 'Pending approval' };
    }
  };

  return (
    <Box minH="100vh" bg="#f5f7fb" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <Box maxW="860px" mx="auto">
        <VStack align="stretch" spacing={6}>
          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3}>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="gray.500" fontWeight="700">
                Utility messages
              </Text>
              <Text fontSize={{ base: '2xl', md: '4xl' }} fontWeight="800" letterSpacing="-0.04em" color="gray.900">
                Manage approved follow-up messages
              </Text>
            </VStack>
            <Button
              leftIcon={<Icon as={Plus as ElementType} boxSize={4} />}
              bg="#1877F2"
              color="white"
              _hover={{ bg: '#1667d9' }}
              borderRadius="16px"
              onClick={() =>
                navigate(
                  `/organization/${orgId}/inbox/facebook/${profileId}/settings/utility-messages/new`,
                )
              }
            >
              New utility message
            </Button>
          </Flex>

          {loading ? (
            <Flex justify="center" py={12}>
              <Spinner color="#1877F2" />
            </Flex>
          ) : templates.length === 0 ? (
            <Box bg="white" border="1px solid" borderColor="blackAlpha.100" borderRadius="24px" px={6} py={10}>
              <VStack spacing={3}>
                <Text fontWeight="700" color="gray.900">
                  No utility messages yet
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center" maxW="420px">
                  Create approved utility messages for conversations where free text is unavailable.
                </Text>
              </VStack>
            </Box>
          ) : (
            <VStack align="stretch" spacing={4}>
              {templates.map((template) => {
                const meta = statusMeta(template.status);
                return (
                  <Box
                    key={template.id}
                    bg="white"
                    border="1px solid"
                    borderColor="blackAlpha.100"
                    borderRadius="22px"
                    px={5}
                    py={5}
                    boxShadow="0 16px 40px rgba(15,23,32,0.05)"
                  >
                    <VStack align="stretch" spacing={3}>
                      <Flex justify="space-between" align="flex-start" gap={4}>
                        <Text fontWeight="600" color="gray.900" lineHeight="1.7">
                          {template.body_text}
                        </Text>
                        <HStack spacing={2}>
                          <Icon as={meta.icon as ElementType} color={`${meta.color}.400`} boxSize={4} />
                          <Badge colorScheme={meta.color} borderRadius="full" px={3} py={1}>
                            {meta.label}
                          </Badge>
                        </HStack>
                      </Flex>
                      <HStack spacing={4} fontSize="sm" color="gray.500" flexWrap="wrap">
                        <Text>{template.trigger_label || template.trigger}</Text>
                        <Text>{template.language?.toUpperCase()}</Text>
                        <Text>Sent {template.total_sent || 0} times</Text>
                      </HStack>
                      {template.status === 'rejected' && template.rejection_reason ? (
                        <Text fontSize="sm" color="red.500">
                          {template.rejection_reason}
                        </Text>
                      ) : null}
                    </VStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
}
